/* Atelier 52: selected galleries remain local; optional previews share one scheduler. */
(() => {
  if (customElements.get('cp-collection-card')) return;
  const cache = new Map();
  let galleryId = 0;

  // A single mobile preview per scroll pause; never a continuously running carousel.
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const previewed = new Set();
  const candidates = new Map();
  let pauseTimer;
  let activePreview;
  const previewAllowed = () => !reducedMotion.matches && !document.hidden && !document.querySelector('dialog[open]') && !navigator.connection?.saveData;
  function cancelAutomaticPreview() {
    clearTimeout(pauseTimer);
    activePreview?.stopPreview();
    activePreview = null;
  }
  function scheduleMobilePreview() {
    cancelAutomaticPreview();
    if (finePointer.matches || !previewAllowed()) return;
    pauseTimer = setTimeout(() => {
      const eligible = [...candidates].filter(([card, ratio]) => ratio >= .7 && card.canPreview() && !card.mobilePreviewShown && !previewed.has(card.dataset.productHandle));
      const centre = window.innerHeight / 2;
      eligible.sort(([a], [b]) => {
        const ar = a.gallery.getBoundingClientRect(), br = b.gallery.getBoundingClientRect();
        return Math.abs(ar.top + ar.height / 2 - centre) - Math.abs(br.top + br.height / 2 - centre);
      });
      if (eligible[0]) {
        activePreview = eligible[0][0];
        activePreview.startPreview(true);
      }
    }, 420);
  }
  window.addEventListener('scroll', scheduleMobilePreview, { passive: true });
  window.addEventListener('pagehide', () => {
    cancelAutomaticPreview();
    document.querySelectorAll('cp-collection-card').forEach(card => card.stopPreview?.());
  });
  document.addEventListener('visibilitychange', () => {
    cancelAutomaticPreview();
    if (document.hidden) document.querySelectorAll('cp-collection-card').forEach(card => card.stopPreview?.());
  });
  reducedMotion.addEventListener('change', () => {
    cancelAutomaticPreview();
    document.querySelectorAll('cp-collection-card').forEach(card => card.stopPreview?.());
  });
  finePointer.addEventListener('change', cancelAutomaticPreview);
  document.addEventListener('click', event => {
    document.querySelectorAll('[data-card-quick][open], [data-card-colours-more][open]').forEach(panel => {
      if (!panel.contains(event.target)) panel.removeAttribute('open');
    });
  });

  function cardRequest(url, handle) {
    const key = url.href;
    const cached = cache.get(key);
    if (cached && Date.now() - cached.time < 120000) return cached.promise;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    const entry = { time: Date.now() };
    entry.promise = fetch(key, { credentials: 'same-origin', signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error('Colour unavailable');
        return response.text();
      })
      .then(html => {
        // Cache only complete sections, allowing failed responses to be retried.
        const card = new DOMParser().parseFromString(html, 'text/html').querySelector('cp-collection-card');
        if (!card || card.dataset.productHandle !== handle || !card.querySelector('[data-card-gallery]')) {
          throw new Error('Incomplete colour response');
        }
        return html;
      })
      .catch(error => {
        if (cache.get(key) === entry) cache.delete(key);
        throw error;
      })
      .finally(() => clearTimeout(timeout));
    cache.set(key, entry);
    if (cache.size > 40) cache.delete(cache.keys().next().value);
    return entry.promise;
  }

  class CollectionCard extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.requestVersion = (this.requestVersion || 0) + 1;
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('keydown', event => this.onKey(event), options);
      this.setupGallery();
    }

    disconnectedCallback() {
      this.requestVersion++;
      this.events?.abort();
      this.teardownGallery();
    }

    teardownGallery() {
      this.stopPreview();
      candidates.delete(this);
      this.previewVisibility?.disconnect();
      if (activePreview === this) activePreview = null;
      this.galleryEvents?.abort();
      this.resize?.disconnect();
      this.visible?.disconnect();
      clearTimeout(this.settleTimer);
      this.drag = null;
      this.suppressClickUntil = 0;
    }

    setupGallery() {
      this.teardownGallery();
      this.gallery = this.querySelector('[data-card-gallery]');
      this.slides = Array.from(this.querySelectorAll('[data-card-slide]'));
      if (!this.gallery || !this.slides.length) return;
      this.index = 0;
      this.pendingIndex = null;
      this.gallery.scrollLeft = 0;
      this.gallery.id = `cp-card-gallery-${++galleryId}`;
      this.gallery.setAttribute('role', 'group');
      this.gallery.setAttribute('aria-roledescription', 'carousel');
      this.gallery.tabIndex = this.slides.length > 1 ? 0 : -1;
      this.querySelectorAll('[data-card-previous], [data-card-next]').forEach(button => {
        button.setAttribute('aria-controls', this.gallery.id);
      });
      this.querySelector('[data-card-controls]').hidden = this.slides.length < 2;
      this.querySelectorAll('[data-cp-card-colour]').forEach(swatch => {
        swatch.setAttribute('role', 'button');
        swatch.setAttribute('aria-pressed', String(swatch.dataset.cardHandle === this.dataset.productHandle));
      });
      this.setAttribute('data-card-ready', '');
      this.updatePosition(0);
      this.galleryEvents = new AbortController();
      const options = { signal: this.galleryEvents.signal, passive: true };
      this.gallery.addEventListener('scroll', () => {
        const width = this.gallery.clientWidth;
        if (!width) return;
        this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.gallery.scrollLeft / width)));
        this.hydrateAround(this.index);
        if (this.pendingIndex === null) this.updatePosition(this.index);
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          this.pendingIndex = null;
          this.updatePosition(this.index);
        }, 160);
      }, options);
      this.gallery.addEventListener('pointerdown', event => {
        this.stopPreview(true);
        this.pendingIndex = null;
        this.updatePosition(this.index);
        // Touch keeps native scrolling; a mouse can drag the same photo rail.
        if (event.pointerType === 'mouse' && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
          this.drag = { id: event.pointerId, x: event.clientX, left: this.gallery.scrollLeft, moved: false };
        }
      }, options);
      this.gallery.addEventListener('pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const delta = event.clientX - this.drag.x;
        if (!this.drag.moved && Math.abs(delta) < 8) return;
        if (!this.drag.moved) {
          this.drag.moved = true;
          this.gallery.setPointerCapture?.(event.pointerId);
          this.gallery.setAttribute('data-dragging', '');
        }
        event.preventDefault();
        this.hydrateAround(Math.max(0, Math.min(this.slides.length - 1, Math.round((this.drag.left - delta) / this.gallery.clientWidth))));
        this.gallery.scrollLeft = this.drag.left - delta;
      }, { signal: this.galleryEvents.signal });
      const finishDrag = event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const moved = this.drag.moved;
        this.drag = null;
        this.gallery.removeAttribute('data-dragging');
        if (moved) {
          this.suppressClickUntil = Date.now() + 400;
          if (this.gallery.hasPointerCapture?.(event.pointerId)) this.gallery.releasePointerCapture(event.pointerId);
          this.goTo(Math.round(this.gallery.scrollLeft / this.gallery.clientWidth));
        }
      };
      this.gallery.addEventListener('pointerup', finishDrag, options);
      this.gallery.addEventListener('pointercancel', finishDrag, options);
      this.gallery.addEventListener('lostpointercapture', finishDrag, options);
      this.gallery.addEventListener('pointerleave', event => {
        if (this.drag && !this.drag.moved) finishDrag(event);
      }, options);
      if ('ResizeObserver' in window) {
        let width = this.gallery.clientWidth;
        this.resize = new ResizeObserver(() => {
          if (width === this.gallery.clientWidth) return;
          width = this.gallery.clientWidth;
          this.index = this.pendingIndex ?? this.index;
          this.pendingIndex = null;
          this.gallery.scrollTo({ left: this.index * width, behavior: 'instant' });
          this.updatePosition(this.index);
        });
        this.resize.observe(this.gallery);
      }
      this.setupQuickAdd();
      this.gallery.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse' || !finePointer.matches || !this.canPreview()) return;
        this.previewTimer = setTimeout(() => this.startPreview(), 180);
      }, options);
      this.gallery.addEventListener('pointerleave', () => this.stopPreview(), options);
      if ('IntersectionObserver' in window) {
        this.previewVisibility = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            candidates.set(this, entry.intersectionRatio);
            if (entry.intersectionRatio < .7) this.stopPreview();
          });
        }, { threshold: [0, .7, 1] });
        this.previewVisibility.observe(this.gallery);
      }
      // Inert templates avoid fetching every photo on a long collection immediately.
      if ('IntersectionObserver' in window) {
        this.visible = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.hydrateAround(this.index);
            this.visible.disconnect();
          }
        }, { rootMargin: '180px' });
        this.visible.observe(this.gallery);
      }
    }

    canPreview() {
      return this.isConnected && this.slides?.length > 1 && this.index === 0 &&
        !this.manualInteraction && !this.hasAttribute('aria-busy') &&
        !this.querySelector('[data-card-quick][open]') && previewAllowed();
    }

    stopPreview(manual = false) {
      if (manual) this.manualInteraction = true;
      this.previewVersion = (this.previewVersion || 0) + 1;
      clearTimeout(this.previewTimer);
      clearTimeout(this.previewEndTimer);
      this.querySelector('.cp-edit-card__preview')?.remove();
      this.removeAttribute('data-card-previewing');
      if (this.slides?.length) this.updateDots(this.pendingIndex ?? this.index ?? 0);
    }

    async startPreview(mobile = false) {
      if (!this.canPreview()) return;
      this.stopPreview();
      const version = this.previewVersion;
      this.hydrateAround(0);
      const source = this.slides[1].querySelector('img');
      if (!source) return;
      const preview = source.cloneNode(true);
      preview.className = 'cp-edit-card__preview';
      preview.alt = '';
      preview.setAttribute('aria-hidden', 'true');
      preview.loading = 'eager';
      try {
        if (preview.decode) await preview.decode();
        else if (!preview.complete || !preview.naturalWidth) return;
      } catch { return; }
      if (version !== this.previewVersion || !this.canPreview()) return;
      this.querySelector('.cp-edit-card__image').append(preview);
      requestAnimationFrame(() => {
        if (version !== this.previewVersion || !this.canPreview()) return;
        this.setAttribute('data-card-previewing', '');
        this.updateDots(1);
      });
      if (mobile) {
        this.mobilePreviewShown = true;
        previewed.add(this.dataset.productHandle);
        // Cap memory on very long browsing sessions without re-previewing mounted cards.
        if (previewed.size > 500) previewed.delete(previewed.values().next().value);
        this.previewEndTimer = setTimeout(() => this.stopPreview(), 1500);
      }
    }

    updateDots(index) {
      this.querySelectorAll('[data-card-dot]').forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
    }

    setupQuickAdd() {
      const colours = this.querySelector('[data-card-colours-more]');
      const disclosureOptions = { signal: this.galleryEvents.signal };
      if (colours) {
        const colourSummary = colours.querySelector('summary');
        const colourLabel = colourSummary.getAttribute('aria-label');
        colours.addEventListener('toggle', () => {
          colourSummary.setAttribute('aria-label', colours.open ? 'Close more colours' : colourLabel);
          if (colours.open) {
            this.stopPreview(true);
            this.querySelector('[data-card-quick]')?.removeAttribute('open');
            document.querySelectorAll('[data-card-colours-more][open]').forEach(other => {
              if (other !== colours) other.removeAttribute('open');
            });
          }
        }, disclosureOptions);
        colours.addEventListener('keydown', event => {
          if (event.key === 'Escape' && colours.open) {
            event.preventDefault();event.stopPropagation();
            colours.removeAttribute('open');colourSummary.focus({ preventScroll: true });
          }
        }, disclosureOptions);
      }
      const panel = this.querySelector('[data-card-quick]');
      if (!panel) return;
      const summary = panel.querySelector('summary');
      const label = summary.getAttribute('aria-label');
      const options = { signal: this.galleryEvents.signal };
      panel.addEventListener('toggle', () => {
        summary.setAttribute('aria-label', panel.open ? 'Close size options' : label);
        if (panel.open) {
          this.stopPreview(true);
          colours?.removeAttribute('open');
          document.querySelectorAll('[data-card-quick][open]').forEach(other => {
            if (other !== panel) other.removeAttribute('open');
          });
        }
      }, options);
      panel.addEventListener('keydown', event => {
        if (event.key === 'Escape' && panel.open) {
          event.preventDefault();
          event.stopPropagation();
          panel.removeAttribute('open');
          summary.focus({ preventScroll: true });
        }
      }, options);
    }

    hydrateAround(index) {
      this.slides.slice(Math.max(0, index - 1), index + 2).forEach(slide => {
        const template = slide.querySelector('template[data-card-image]');
        if (template) template.replaceWith(template.content.cloneNode(true));
      });
    }

    updatePosition(index) {
      const counter = this.querySelector('[data-card-counter]');
      if (counter) counter.textContent = `Photo ${index + 1} of ${this.slides.length}`;
      this.updateDots(index);
      this.querySelector('[data-card-previous]').disabled = index === 0;
      this.querySelector('[data-card-next]').disabled = index === this.slides.length - 1;
      this.slides.forEach((slide, i) => { slide.tabIndex = i === index ? 0 : -1; });
    }

    goTo(index) {
      this.stopPreview(true);
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      this.hydrateAround(target);
      this.pendingIndex = target;
      this.updatePosition(target);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.gallery.scrollTo({ left: target * this.gallery.clientWidth, behavior: reducedMotion ? 'instant' : 'smooth' });
    }

    onClick(event) {
      if (Date.now() < (this.suppressClickUntil || 0) && this.gallery?.contains(event.target)) {
        event.preventDefault();
        this.suppressClickUntil = 0;
        return;
      }
      const swatch = event.target.closest('[data-cp-card-colour]');
      if (swatch && this.contains(swatch)) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.selectColour(swatch);
        return;
      }
      const button = event.target.closest('[data-card-previous], [data-card-next]');
      if (button && this.contains(button) && !button.disabled) {
        event.preventDefault();
        this.goTo((this.pendingIndex ?? this.index) + (button.hasAttribute('data-card-next') ? 1 : -1));
      }
    }

    onKey(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const swatch = event.target.closest('[data-cp-card-colour]');
      if (swatch && event.key === ' ') {
        event.preventDefault();
        this.selectColour(swatch);
        return;
      }
      if (!this.gallery?.contains(event.target)) return;
      let target = this.pendingIndex ?? this.index;
      if (event.key === 'ArrowRight') target++;
      else if (event.key === 'ArrowLeft') target--;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = this.slides.length - 1;
      else return;
      event.preventDefault();
      this.goTo(target);
      this.gallery.focus({ preventScroll: true });
    }

    async selectColour(swatch) {
      if (this.querySelector('[data-cp32-add][aria-busy=true]')) return;
      this.stopPreview(true);
      this.querySelector('[data-card-quick]')?.removeAttribute('open');
      const requestVersion = ++this.requestVersion;
      const handle = swatch.dataset.cardHandle;
      const status = this.querySelector('[data-card-status]');
      this.querySelector('[data-card-error]').hidden = true;
      if (handle === this.dataset.productHandle) {
        this.removeAttribute('aria-busy');
        status.textContent = '';
        return;
      }
      const url = new URL(swatch.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      url.searchParams.set('section_id', 'cp-collection-card');
      const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      const colour = swatch.getAttribute('aria-label');
      this.setAttribute('aria-busy', 'true');
      status.textContent = `Loading ${colour}…`;
      try {
        const html = await cardRequest(url, handle);
        if (!this.isConnected || requestVersion !== this.requestVersion) return;
        const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector('cp-collection-card');
        if (!replacement || replacement.dataset.productHandle !== handle) throw new Error('Unexpected product');
        const restoreFocus = this.contains(document.activeElement);
        this.teardownGallery();
        this.replaceChildren(...Array.from(replacement.childNodes));
        this.dataset.productHandle = handle;
        this.setupGallery();
        this.querySelector('[data-card-status]').textContent = `${colour} selected. ${this.slides.length} photos available.`;
        if (restoreFocus) {
          Array.from(this.querySelectorAll('[data-cp-card-colour]'))
            .find(link => link.dataset.cardHandle === handle)?.focus({ preventScroll: true });
        }
      } catch (error) {
        if (!this.isConnected || requestVersion !== this.requestVersion) return;
        this.querySelector('[data-card-fallback]').href = swatch.href;
        this.querySelector('[data-card-error]').hidden = false;
        this.querySelector('[data-card-status]').textContent = `Couldn’t load ${colour}. Try again or open its product page.`;
      } finally {
        if (requestVersion === this.requestVersion) this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('cp-collection-card', CollectionCard);
})();
